import React, { Fragment, useReducer } from "react";
import ReactModal from "react-modal";
import moment from "moment-timezone";
import { toast } from "react-toastify";

import TextAreaInput from "../textarea-input";
import shortenName from "../../helpers/shorten-name";

const toastOptions = {
  position: "top-right",
  autoClose: 5000,
  hideProgressBar: false,
  closeOnClick: true,
  pauseOnHover: true,
  draggable: true,
};


const reducer = (state, action) => {
  switch (action.type) {
    case "fieldChanged": {
      return {
        ...state,
        [action.field]: action.value,
      };
    }
    case "openModal": {
      return {
        ...state,
        showModal: true,
        ...(action.payload || {}),
      };
    }
    case "openDeleteModal": {
      return {
        ...state,
        showDeleteModal: true,
        ...(action.payload || {}),
      };
    }
    case "closeModal": {
      return {
        ...state,
        showModal: false,
        showDeleteModal: false,
        _id: undefined,
        note: "",
      };
    }
    default:
      return state;
  }
};

const SingleProspectNotesTable = (props) => {
  const { notes = [], handleChange, handleToggleEdit, disabled = false } = props;

  const initialState = {
    showModal: false,
    showDeleteModal: false,
    saving: false,
    _id: undefined,
    note: "",
  };

  const [state, dispatch] = useReducer(reducer, initialState);

  const handleAddNote = (e) => {
    e.preventDefault();

    handleToggleEdit("notes");
    dispatch({
      type: "openModal",
    });
  };

  const handleEditNote = (e, index) => {
    e.preventDefault();

    const note = notes[index];
    if (!note) {
      return;
    }

    handleToggleEdit("notes");
    dispatch({
      type: "openModal",
      payload: note,
    });
  };

  const handleDeleteNote = (e, index) => {
    e.preventDefault();

    const note = notes[index];
    if (!note) {
      return;
    }

    handleToggleEdit("notes");
    dispatch({
      type: "openDeleteModal",
      payload: note,
    });
  };

  const handleCloseModal = () => {
    handleToggleEdit();
    dispatch({
      type: "closeModal",
    });
  };

  const handleInputChange = (e) => {
    dispatch({
      type: "fieldChanged",
      field: e.currentTarget.name,
      value: e.currentTarget.value,
    });
  };

  const handleUpdateNote = async (e) => {
    e.preventDefault();

    if (!state.note) {
      return toast.error(
        "Please fill in all required fields and retry.",
        toastOptions,
      );
    }

    dispatch({
      type: "fieldChanged",
      field: "saving",
      value: true,
    });
    if (!state._id) {
      await handleChange([...(notes || []), {
          _id: state._id,
          note: state.note,
        }],
      );
    } else {
      const originalNote = notes.find(({ _id }) => _id === state._id);
      if (originalNote.note !== state.note) {
        await handleChange(
          (notes || []).map((item) => item._id === state._id ? { ...item, note: state.note, isUpdated: true } : item),
        );
      }
    }
    dispatch({
      type: "fieldChanged",
      field: "saving",
      value: false,
    });

    handleCloseModal();
  };

  const handleDeleteNoteConfirm = async (e) => {
    e.preventDefault();

    if (!state._id) {
      return;
    }

    dispatch({
      type: "fieldChanged",
      field: "saving",
      value: true,
    });
    await handleChange((notes || []).filter((item) => item._id !== state._id));
    dispatch({
      type: "fieldChanged",
      field: "saving",
      value: false,
    });

    handleCloseModal();
  };

  return (
    <Fragment>
      <h4 className="mt-20">Saved Notes</h4>

      <div className="prospect-table prospect-notes-table">
        <table>
          <thead>
          <tr>
            <th>Create Date</th>
            <th>Note</th>
            <th>Entered By</th>
            <th>Updated By</th>
            <th>Actions</th>
          </tr>
          </thead>
          <tbody>
          {!notes?.length && <tr>
            <td colSpan={4}>No notes are saved yet</td>
            <td/>
          </tr>}
          {notes?.map((({ _id, createdAt, createdBy, note, updatedBy }, index) => (
            <tr key={_id}>
              <td>{moment(createdAt).format("M/D/YYYY")}</td>
              <td>{note}</td>
              <td>{shortenName(createdBy?.name)}</td>
              <td>{updatedBy?.name ? shortenName(updatedBy?.name) : "-"}</td>
              <td>
                <button
                  className="base-button icon-button evo-blue"
                  onClick={(e) => {
                    handleEditNote(e, index);
                  }}
                  disabled={disabled}
                >
                  <span className="material-icons">edit</span>
                </button>

                <button
                  className="base-button icon-button btn-danger ml-2"
                  onClick={(e) => {
                    handleDeleteNote(e, index);
                  }}
                  disabled={disabled}
                >
                  <span className="material-icons">delete</span>
                </button>
              </td>
            </tr>
          )))}
          </tbody>
        </table>
      </div>

      <button
        className="mt-20 action-button base-button base-blue-button"
        onClick={handleAddNote}
        disabled={disabled}
      >
        Add Note
      </button>

      <ReactModal
        isOpen={state.showModal}
        shouldCloseOnEsc={false}
        shouldCloseOnOverlayClick={false}
        onRequestClose={handleCloseModal}
        style={{
          overlay: {
            zIndex: 999999,
            backgroundColor: "rgb(32 53 68 / 50%)",
          },
          content: {
            width: "auto",
            border: "none",
            overflowY: "scroll",
            maxHeight: "100vh",
            minWidth: "50vw",
            borderRadius: "0",
            boxShadow: "10px 10px 91px -17px rgb(0 0 0 / 25%)",
          },
        }}
        appElement={document.getElementById("app")}
      >
        <div className="modal-header base-modal">
          <h4>{state._id ? "Edit" : "Add"} Note</h4>
        </div>

        <div className="modal-body base-modal overflow-scroll">
          <form onSubmit={handleUpdateNote} className="w-100 base-form mt-20">
            <TextAreaInput
              type="text"
              name="note"
              value={state.note}
              label="Note"
              placeholder="Add note"
              handleChange={handleInputChange}
              required
            />

            <div className="w-100 mt-20 d-flex gap-20">
              <button className="base-button evo-blue flex-half" type="submit" disabled={state.saving}>
                Submit
              </button>

              <button className="base-button btn-danger flex-half" type="button" onClick={handleCloseModal}
                      disabled={state.saving}>
                Cancel
              </button>
            </div>
          </form>
        </div>
      </ReactModal>

      <ReactModal
        isOpen={state.showDeleteModal}
        shouldCloseOnEsc={false}
        shouldCloseOnOverlayClick={false}
        onRequestClose={handleCloseModal}
        style={{
          overlay: {
            zIndex: 999999,
            backgroundColor: "rgb(32 53 68 / 50%)",
          },
          content: {
            width: "auto",
            padding: "40px",
            border: "none",
            maxWidth: "700px",
            maxHeight: "50%",
            borderRadius: "0",
            boxShadow: "10px 10px 91px -17px rgb(0 0 0 / 25%)",
          },
        }}
        appElement={document.getElementById("app")}
      >
        <div className="modal-header base-modal">
          <h4>Delete Note</h4>
        </div>

        <div className="modal-body base-modal overflow-scroll">
          <div className="w-100 mt-20">
            This note will be permanently deleted from the passenger's profile.<br/>
            Are you sure you want to continue?
          </div>

          <div className="w-100 mt-20 d-flex gap-20">
            <button className="base-button evo-blue flex-half" type="button" onClick={handleCloseModal}
                    disabled={state.saving}>
              Cancel
            </button>

            <button className="base-button btn-danger flex-half" type="button" onClick={handleDeleteNoteConfirm}
                    disabled={state.saving}>
              Delete Note
            </button>
          </div>
        </div>
      </ReactModal>
    </Fragment>
  );
};

export default SingleProspectNotesTable;
